import { Box, IconButton, Typography } from "@mui/material";
import React, { FC } from "react";

import IcIcon from "../../../IcIcon";
import { TDynamicVariable } from "@shared/types";

type TOperator = {
  key: string;
  icon?: string;
  text?: string;
  iconLikeText?: string;
  variable: TDynamicVariable;
};

const operators: TOperator[] = [
  {
    key: "add",
    icon: "mdi:add",
    variable: {
      variableType: "OPERATOR",
      operator: "+",
    },
  },
  {
    key: "subtract",
    icon: "mdi:minus",
    variable: {
      variableType: "OPERATOR",
      operator: "-",
    },
  },
  {
    key: "multiply",
    icon: "mdi:multiply",
    variable: {
      variableType: "OPERATOR",
      operator: "*",
    },
  },
  {
    key: "divide",
    icon: "ic:round-divide",
    variable: {
      variableType: "OPERATOR",
      operator: "/",
    },
  },
  
  {
    key: "round_open_bracket",
    iconLikeText: "Round(",
    variable: {
      variableType: "OPERATOR",
      operator: "round(",
    },
  },
  {
    key: "floor_open_bracket",
    iconLikeText: "Floor(",
    variable: {
      variableType: "OPERATOR",
      operator: "floor(",
    },
  },
  {
    key: "ceil_open_bracket",
    iconLikeText: "Ceil(",
    variable: {
      variableType: "OPERATOR",
      operator: "ceil(",
    },
  }, 
  
  {
    key: "open_bracket",
    iconLikeText: "(",
    variable: {
      variableType: "OPERATOR",
      operator: "(",
    },
  },
  {
    key: "close_bracket",
    iconLikeText: ")",
    variable: {
      variableType: "OPERATOR",
      operator: ")",
    },
  },
  {
    key: "comma",
    iconLikeText: ",",
    variable: {
      variableType: "OPERATOR",
      operator: ",",
    },
  }, 
];

export const operatorSigns: string[] = operators
  .map((o) => o.variable.operator)
  .filter((s): s is NonNullable<typeof s> => s != null && s.length > 0);

type Props = {
  onVariableSelect?: (variable: TDynamicVariable) => void;
  addButton?: React.ReactNode;
};

const basicOperators = operators.filter(o => ['add', 'subtract', 'multiply', 'divide'].includes(o.key));
const functionOperators = operators.filter(o => ['round_open_bracket', 'floor_open_bracket', 'ceil_open_bracket'].includes(o.key));
const bracketOperators = operators.filter(o => ['open_bracket', 'close_bracket', 'comma'].includes(o.key));

const OperatorButton: FC<{ op: TOperator; onClick: () => void }> = ({ op, onClick }) => (
  <IconButton
    onClick={onClick}
    size="small"
    sx={{
      borderRadius: "6px",
      border: "1px solid #e0e0e0",
      backgroundColor: "#ffffff",
      minWidth: op.iconLikeText ? (op.iconLikeText.length * 8) + 24 : 36,
      height: 32,
      mx: 0.25,
      "&:hover": {
        backgroundColor: "#f0f7ff",
        borderColor: "#2196f3",
      },
    }}
  >
    {op.icon ? (
      <IcIcon noWrap icon={op.icon} style={{ fontSize: 16 }} />
    ) : (
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1a3a5c" }}>
        {op.iconLikeText}
      </Typography>
    )}
  </IconButton>
);

const ArithmeticOperators: FC<Props> = ({ onVariableSelect, addButton }) => {
  return (
    <>
      {/* Basic Math */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: 11, color: "#6c757d", mr: 1, fontWeight: 500, textTransform: "uppercase" }}>
          Math
        </Typography>
        {basicOperators.map((op) => (
          <OperatorButton
            key={op.key}
            op={op}
            onClick={() => onVariableSelect?.(op.variable)}
          />
        ))}
      </Box>

      <Box sx={{ width: "1px", height: 20, backgroundColor: "#d0d0d0", mx: 1 }} />

      {/* Functions */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: 11, color: "#6c757d", mr: 1, fontWeight: 500, textTransform: "uppercase" }}>
          Functions
        </Typography>
        {functionOperators.map((op) => (
          <OperatorButton
            key={op.key}
            op={op}
            onClick={() => onVariableSelect?.(op.variable)}
          />
        ))}
      </Box>

      <Box sx={{ width: "1px", height: 20, backgroundColor: "#d0d0d0", mx: 1 }} />

      {/* Brackets */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: 11, color: "#6c757d", mr: 1, fontWeight: 500, textTransform: "uppercase" }}>
          Group
        </Typography>
        {bracketOperators.map((op) => (
          <OperatorButton
            key={op.key}
            op={op}
            onClick={() => onVariableSelect?.(op.variable)}
          />
        ))}
      </Box>

      {addButton && (
        <>
          <Box sx={{ width: "1px", height: 20, backgroundColor: "#d0d0d0", mx: 1 }} />
          {addButton}
        </>
      )}
    </>
  );
};

export default ArithmeticOperators;
